/* global updateClientGroupSecondSubcategoryUrl updateClientGroupSportUrl */

import React, { useState, useCallback, useEffect, useRef } from 'react';

import { Modal } from 'flowbite';

import { formatFigure, formatToFixedMoneyWithLocale } from 'Helpers/formatService';
import { getIntlLocale } from 'Helpers/localeService';
import { modalOptions } from 'Helpers/modalOptions';

import ConfirmationModal from 'CommonComponents/ConfirmationModal';
import NumberFormat from 'CommonComponents/LocalisedNumberFormat';
import TextButton from 'CommonComponents/TextButton';
import Tooltip from 'CommonComponents/Tooltip';

import { useToast } from 'Hooks/useToast';

import CircleCheckIcon from 'Assets/icons/circle-check.svg';
import EditIcon from 'Assets/icons/edit.svg';
import TrashIcon from 'Assets/icons/trash.svg';

export default function ClientGroupNumberSecondSubcategoryRow(props) {
    const toast = useToast();
    const [editing, setEditing] = useState(props.secondSubcategory.isNew ?? false);
    const [inlineEditing, setInlineEditing] = useState(false);
    const [deleted, setDeleted] = useState(props.secondSubcategory.isDeleted ?? false);
    const [name, setName] = useState(props.secondSubcategory.name);
    const [value, setValue] = useState(props.secondSubcategory.value ?? 0);
    const [inlineValue, setInlineValue] = useState(null);

    const [actionCompleted, setActionCompleted] = useState(false);
    const [actionInProgress, setActionInProgress] = useState(false);
    const [actionFailed, setActionFailed] = useState(false);

    const [categoryDeleteModalElement, setCategoryDeleteModalElement] = useState(null);
    const categoryDeleteModalRef = useRef();

    const cleanCategoryDeleteModalStates = useCallback(() => {
        setActionInProgress(false);
        setActionFailed(false);
        setActionCompleted(false);
    }, [actionInProgress, actionFailed, actionCompleted]);

    const hideModalElement = (modalElement) => {
        modalElement && modalElement.hide();
    };

    const successHandlerDelete = () => {
        hideModalElement(categoryDeleteModalElement);
        setDeleted(true);
        props.secondSubcategory.value = 0;
        props.secondSubcategory.isDeleted = true;
        props.calculateSubcategoryTotal();
    };

    const triggerDelete = (category_id) => {
        setActionInProgress(true);
        deleteSecondSubcategory(category_id);
    };

    const closeModal = () => {
        hideModalElement(categoryDeleteModalElement);
    };

    const toggleEditing = () => {
        props.setError('');
        setEditing(!editing);
    };

    const handleChangeName = (event) => {
        setName(event.target.value);
    };

    const handleChangeValue = (values) => {
        setValue(values.floatValue);
    };

    const saveEditing = (second_subcategory_id) => {
        props.setError('');
        props.secondSubcategory.isNew = false;

        const doSave = () => {
            axios
                .put(updateClientGroupSecondSubcategoryUrl.replace('CLIENT_GROUP_SECOND_SUBCATEGORY_ID', second_subcategory_id), {
                    name: name,
                    value: value,
                })
                .then((res) => {
                    props.secondSubcategory.name = res.data.clientGroupSecondSubcategory.name;
                    props.secondSubcategory.value = res.data.clientGroupSecondSubcategory.value;
                    props.calculateSubcategoryTotal();
                    setEditing(false);
                })
                .catch(() => {
                    props.setError('There was an error editing the Sub Category');
                });
        };

        // If quantity linking is enabled and we have the parent subcategory info,
        // show the downstream impact dialog before saving.
        if (window.quantityLinkEnabled && window.quantityLinkDriverImpactPreviewUrl && props.parentSubcategoryId && props.secondSubcategory.event_id) {
            const oldSecondValue = props.secondSubcategory.value ?? 0;
            const parentCurrentValue = props.parentSubcategoryValue ?? 0;
            const newParentValue = parentCurrentValue - oldSecondValue + (value ?? 0);

            const baseUrl = window.quantityLinkDriverImpactPreviewUrl;
            const params = new URLSearchParams({
                source_type: 'App\\ClientGroupSubcategory',
                source_id: String(props.parentSubcategoryId),
            });
            const previewUrl = baseUrl + '?' + params.toString();

            window.dispatchEvent(
                new CustomEvent('quantityimpact:check', {
                    detail: {
                        previewUrl: previewUrl,
                        newValue: newParentValue,
                        formEl: null,
                        onConfirm: doSave,
                        sourceLabel: props.secondSubcategory.name,
                    },
                }),
            );
        } else {
            doSave();
        }
    };

    const deleteSecondSubcategory = (second_subcategory_id) => {
        props.setError('');

        axios
            .delete(updateClientGroupSecondSubcategoryUrl.replace('CLIENT_GROUP_SECOND_SUBCATEGORY_ID', second_subcategory_id))
            .then((res) => {
                setActionCompleted(true);
            })
            .catch((error) => {
                setActionFailed(true);
                props.setError('There was an error deleting the Sub Category');
            });
    };

    useEffect(() => {
        if (!deleted) {
            setCategoryDeleteModalElement(new Modal(categoryDeleteModalRef.current, modalOptions(false)));
        }
    }, [deleted]);

    const showDeleteCategory = () => {
        categoryDeleteModalElement && categoryDeleteModalElement.show();
    };

    // ── Override helpers (for event_id === null second subcategories) ──────
    // Two flavours of system rows:
    //  1. DB-backed leaf rows  → override keyed by `second_subcategory_<id>`
    //  2. Dynamic per-sport rows (athletes/team_officials/td/ito/nto by Sport)
    //     → override keyed by `sport_<slug>_<sportId>`; no DB row backs them.
    const isSportRow = !!(props.sportSlug && props.sportId);
    const overrideKey = isSportRow ? `sport_${props.sportSlug}_${props.sportId}` : `second_subcategory_${props.secondSubcategory.id}`;
    const currentOverride = props.overrides ? props.overrides[overrideKey] : null;

    // Leaf rows that may carry an override:
    //  - DB-backed second subcategory with no event_id (system row with an id)
    //  - dynamic per-sport rows
    const isOverridableLeaf = isSportRow || (!props.secondSubcategory.event_id && !!props.secondSubcategory.id);

    const displayValue = () => {
        if (isOverridableLeaf && currentOverride) return currentOverride.value;
        if (!props.secondSubcategory.event_id) return props.secondSubcategory.total;
        return props.secondSubcategory.value;
    };

    const handleInlineClick = () => {
        if (!isOverridableLeaf || !props.canUpdate) return;
        setInlineValue(displayValue() ?? 0);
        setInlineEditing(true);
    };

    const handleInlineCancel = () => {
        setInlineEditing(false);
        setInlineValue(null);
    };

    const overrideEndpoint = (body) => {
        if (isSportRow) {
            return axios.put(updateClientGroupSportUrl.replace('SPORT_ID', props.sportId), {
                slug: props.sportSlug,
                ...body,
            });
        }
        return axios.put(updateClientGroupSecondSubcategoryUrl.replace('CLIENT_GROUP_SECOND_SUBCATEGORY_ID', props.secondSubcategory.id), body);
    };

    const saveInlineOverride = () => {
        const newValue = inlineValue ?? 0;
        overrideEndpoint({ value: newValue })
            .then((res) => {
                setInlineEditing(false);
                setInlineValue(null);
                if (res.data.previous_overrider) {
                    const prev = res.data.previous_overrider;
                    toast.warning(
                        $t('Previous override by {{name}} on {{date}} was replaced', {
                            name: prev.name || $t('Unknown'),
                            date: prev.at ? new Date(prev.at).toLocaleDateString(getIntlLocale()) : '',
                        }),
                    );
                }
                if (props.onOverrideChange) {
                    props.onOverrideChange(overrideKey, res.data.override);
                }
                props.calculateSubcategoryTotal();
            })
            .catch(() => {
                props.setError($t('There was an error saving the override'));
            });
    };

    const resetOverride = (e) => {
        e.preventDefault();
        overrideEndpoint({ reset_override: true })
            .then(() => {
                if (props.onOverrideChange) {
                    props.onOverrideChange(overrideKey, null);
                }
                props.calculateSubcategoryTotal();
            })
            .catch(() => {
                props.setError($t('There was an error resetting the override'));
            });
    };

    const isOverridden = isOverridableLeaf && !!currentOverride;

    return !deleted ? (
        <>
            <tr
                key={props.secondSubcategory.id}
                className={`tw-border-b-2 last:tw-border-0 tw-border-grey ${isOverridden ? '!tw-bg-amber-50' : '!tw-bg-white'}`}
            >
                {/* Name Section */}

                {!editing && (
                    <td className="!tw-pl-9 tw-pr-4 tw-py-4 tw-border-r-2">
                        <div className="tw-flex tw-items-center tw-gap-x-2">
                            <span>{props.secondSubcategory.name}</span>
                            {isOverridden && (
                                <span className="tw-inline-flex tw-items-center tw-gap-x-1 tw-px-2 tw-py-0.5 tw-rounded-full tw-text-xs tw-font-medium tw-bg-amber-100 tw-text-amber-700">
                                    <EditIcon className="tw-w-2.5 tw-h-2.5" />
                                    {$t('Overridden')}
                                </span>
                            )}
                        </div>
                    </td>
                )}
                {editing && (
                    <td className="!tw-pl-9 tw-pr-4 tw-py-4 tw-border-r-2">
                        <input
                            type="text"
                            step="1"
                            name={`client_group_subcategory_${props.secondSubcategory.id}`}
                            className="tw-w-full tw-rounded-lg tw-bg-input-background tw-text-input-text tw-font-semibold tw-border-0 tw-py-1"
                            id={`client_group_subcategory_${props.secondSubcategory.id}`}
                            value={name}
                            onChange={(event) => handleChangeName(event)}
                        />
                    </td>
                )}

                {/* Value Section */}

                <td className="inbud-numeric-cell">
                    {inlineEditing && (
                        <NumberFormat
                            id={`inline_override_second_${props.secondSubcategory.id}`}
                            value={inlineValue}
                            defaultValue={0}
                            className="tw-w-full tw-rounded-lg tw-bg-input-background tw-text-input-text tw-font-semibold tw-border-0 tw-py-1 inbud-numeric-text"
                            decimalScale={0}
                            thousandSeparator={true}
                            inputMode="numeric"
                            allowNegative={false}
                            autoFocus
                            onValueChange={(vals) => setInlineValue(vals.floatValue ?? 0)}
                            onKeyDown={(e) => {
                                if (e.key === 'Enter') saveInlineOverride();
                                if (e.key === 'Escape') handleInlineCancel();
                            }}
                        />
                    )}
                    {!inlineEditing && (
                        <div>
                            <Tooltip tooltipType="text" tooltipContent={$t('Click to override')} allowTooltip={isOverridableLeaf && props.canUpdate}>
                                <span
                                    onClick={handleInlineClick}
                                    className={isOverridableLeaf && props.canUpdate ? 'tw-cursor-pointer hover:tw-underline' : ''}
                                >
                                    {formatFigure(displayValue())}
                                    {!!props.secondSubcategory.event_id && editing && (
                                        <NumberFormat
                                            id={`client_group_subcategory_${props.secondSubcategory.id}`}
                                            name={`client_group_subcategory_${props.secondSubcategory.id}`}
                                            value={value}
                                            defaultValue={0}
                                            className="tw-w-full tw-rounded-lg tw-bg-input-background tw-text-input-text tw-font-semibold tw-border-0 tw-py-1 inbud-numeric-text"
                                            decimalScale={10}
                                            thousandSeparator={true}
                                            inputMode="numeric"
                                            allowNegative={false}
                                            onValueChange={(values) => handleChangeValue(values)}
                                        />
                                    )}
                                </span>
                            </Tooltip>
                            {isOverridden && currentOverride && (
                                <div className="tw-mt-0.5">
                                    <a href="#" onClick={resetOverride} className="tw-text-xs tw-text-charcoal-50 hover:tw-text-charcoal-75 tw-underline">
                                        {$t('Reset to {{value}}', { value: formatToFixedMoneyWithLocale(currentOverride.source_value) })}
                                    </a>
                                </div>
                            )}
                        </div>
                    )}
                    {inlineEditing && (
                        <div className="tw-flex tw-items-center tw-gap-x-2 tw-mt-1">
                            <button
                                type="button"
                                onClick={saveInlineOverride}
                                className="tw-text-xs tw-text-blue tw-font-medium tw-underline tw-border-0 tw-bg-transparent tw-cursor-pointer tw-p-0"
                            >
                                {$t('Save')}
                            </button>
                            <button
                                type="button"
                                onClick={handleInlineCancel}
                                className="tw-text-xs tw-text-charcoal-50 tw-font-medium tw-underline tw-border-0 tw-bg-transparent tw-cursor-pointer tw-p-0"
                            >
                                {$t('Cancel')}
                            </button>
                        </div>
                    )}
                </td>

                <td className="tw-p-4">
                    <div className="tw-flex tw-justify-between">
                        {!!props.secondSubcategory.event_id && (
                            <div className="tw-flex tw-items-center tw-gap-x-3">
                                {editing && !!props.canUpdate && (
                                    <TextButton variant="primary" onClick={() => saveEditing(props.secondSubcategory.id)}>
                                        <CircleCheckIcon className="tw-w-3 tw-h-3" /> {$t('Save')}
                                    </TextButton>
                                )}
                                {!editing && !!props.canUpdate && (
                                    <TextButton onClick={() => toggleEditing()}>
                                        <EditIcon className="tw-w-3 tw-h-3" /> {$t('Edit')}
                                    </TextButton>
                                )}
                                {!!props.canDelete && (
                                    <TextButton variant="danger" onClick={() => showDeleteCategory(props.secondSubcategory.id)}>
                                        <TrashIcon className="tw-w-3 tw-h-3" /> {$t('Delete')}
                                    </TextButton>
                                )}
                            </div>
                        )}
                    </div>
                    <ConfirmationModal
                        closeHandler={closeModal}
                        successHandler={successHandlerDelete}
                        triggerAction={() => triggerDelete(props.secondSubcategory.id)}
                        cleanStates={cleanCategoryDeleteModalStates}
                        allowAction={true}
                        modalId={`delete-second-subcategory-${props.secondSubcategory.id}`}
                        modalRef={categoryDeleteModalRef}
                        modalElement={categoryDeleteModalElement}
                        request={actionInProgress}
                        actionCompleted={actionCompleted}
                        actionFailed={actionFailed}
                        confirmationMsg={$t('Please confirm you want to delete {{name}} as this action cannot be reversed', {
                            name: props.secondSubcategory.name,
                        })}
                        actionCompletedMsg={$t('{{name}} deleted', { name: props.secondSubcategory.name })}
                        actionFailedMsg={$t('{{name}} could not be deleted', { name: props.secondSubcategory.name })}
                        title={$t('Delete Sub Category')}
                        cancelButtonText="Cancel"
                        primaryButtonText="Delete"
                    />
                </td>
            </tr>
        </>
    ) : (
        <></>
    );
}
